import React from 'react';
import { Form, Field } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { useSelector, useDispatch } from 'react-redux';
import arrayMutators from 'final-form-arrays';
import { RootState } from '../../../../store';
import DocumentFileItem from './DocumentFileItem';
import {
  DocumentCategory,
  TranscriptType,
  FilingType,
  LetterType,
  PresentationType,
} from '../../../../store/document/types';
import styles from './DocumentUploadForm.module.scss';
import Select from '../../../../components/UI/Select/Select';
import { Heading } from '../../../../components/Typography';
import Button from '../../../../components/UI/Button/Button';
import TickerInput from '../../../../components/TickerInput';
import {
  setCompany,
  setCategory,
  setType,
  setFiles,
  setUploading,
  UploadFile,
  resetUploadState,
} from '../../../../store/documentUpload/slice';
import DocumentDropzone from './DocumentDropzone';
import { uploadDocuments, UploadDocumentsResponse } from '../../../../api/documentUploadApi';
import { Company } from '../../../../store/company/types';

type TypeOption = { value: string; label: string };

const typeOptionsMap: Record<DocumentCategory, TypeOption[]> = {
  [DocumentCategory.TRANSCRIPT]: [
    { value: TranscriptType.EARNINGS_CALL, label: 'Earnings Call' },
    { value: TranscriptType.SPECIAL, label: 'Special' },
  ],
  [DocumentCategory.FILING]: [
    { value: FilingType.TEN_K, label: '10-K' },
    { value: FilingType.TWENTY_F, label: '20-F' },
    { value: FilingType.TEN_Q, label: '10-Q' },
    { value: FilingType.EIGHT_K, label: '8-K' },
    { value: FilingType.SIX_K, label: '6-K' },
    { value: FilingType.EXHIBIT, label: 'Exhibit' },
    { value: FilingType.EARNINGS_RELEASE, label: 'Earnings Release' },
  ],
  [DocumentCategory.LETTER]: [
    { value: LetterType.SHAREHOLDER_LETTER, label: 'Shareholder Letter' },
  ],
  [DocumentCategory.PRESENTATION]: [
    { value: PresentationType.INVESTOR, label: 'Investor Presentation' },
  ],
};

const categoryOptions = [
  { value: DocumentCategory.TRANSCRIPT, label: 'Transcript' },
  { value: DocumentCategory.FILING, label: 'Filing' },
  { value: DocumentCategory.LETTER, label: 'Letter' },
  { value: DocumentCategory.PRESENTATION, label: 'Presentation' },
];

const validateFile = (file: UploadFile | undefined) => {
  if (!file) return undefined;
  if (!file.name || !file.fiscalYear || !file.fiscalQuarter || !file.publishedAt)
    return 'All fields required';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(file.publishedAt)) return 'Date must be YYYY-MM-DD';
  if (isNaN(Number(file.fiscalYear)) || isNaN(Number(file.fiscalQuarter)))
    return 'Year/Quarter must be numbers';
  return undefined;
};

function isResultsResponse(
  res: UploadDocumentsResponse,
): res is { results: { name: string; status: string; error?: string }[] } {
  return (res as any).results !== undefined;
}

// Helper to normalize file names for matching
function normalizeName(name: string) {
  return name
    .trim()
    .replace(/\.pdf$/i, '')
    .toLowerCase();
}

const DocumentUploadForm: React.FC = () => {
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) => state.documentUpload.company);
  const category = useSelector((state: RootState) => state.documentUpload.category);
  const type = useSelector((state: RootState) => state.documentUpload.type);
  const reduxFiles = useSelector((state: RootState) => state.documentUpload.files);
  const uploading = useSelector((state: RootState) => state.documentUpload.uploading);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  // useDropzone must be at the top level
  const dropzoneRef = React.useRef<FieldArrayRenderProps<UploadFile, HTMLElement> | null>(null);
  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      if (!dropzoneRef.current) return;
      const fields = dropzoneRef.current.fields;
      const newFiles = acceptedFiles
        .filter((file) => file.type === 'application/pdf')
        .map((file) => ({
          file,
          name: file.name.replace(/\.pdf$/i, ''),
          fiscalYear: '',
          fiscalQuarter: '',
          publishedAt: '',
          status: 'idle' as const,
        }));
      newFiles.forEach((f) => fields.push(f));
      // Also update redux
      dispatch(setFiles([...(reduxFiles || []), ...newFiles]));
    },
    [dispatch, reduxFiles],
  );

  const lastValues = React.useRef<any>({});

  // Set default category and type if not set
  React.useEffect(() => {
    if (!category) {
      dispatch(setCategory(categoryOptions[0].value));
    }
  }, [category, dispatch]);
  React.useEffect(() => {
    if (category && !type) {
      const firstType = typeOptionsMap[category as DocumentCategory]?.[0]?.value;
      if (firstType) dispatch(setType(firstType));
    }
  }, [category, type, dispatch]);

  // Clear form handler
  const handleClearForm = (form: any) => {
    dispatch(resetUploadState());
    form.reset();
  };

  // Sync redux state with form state on change
  const handleCompanyChange = (val: Company | null) => {
    // If ticker changes, clear the form
    dispatch(resetUploadState());
    // Set the new ticker
    dispatch(setCompany(val));
  };
  const handleCategoryChange = (val: string) => {
    dispatch(setCategory(val));
    // Set type to first available for new category
    const firstType = typeOptionsMap[val as DocumentCategory]?.[0]?.value;
    dispatch(setType(firstType || ''));
  };
  const handleTypeChange = (val: string) => dispatch(setType(val));

  return (
    <Form
      onSubmit={async (values, form) => {
        // Remove files with status 'success' before uploading again
        const filteredFiles = (values.files || []).filter(
          (f: UploadFile | undefined): f is UploadFile => !!f && f.status !== 'success',
        );
        if (filteredFiles.length !== (values.files || []).length) {
          form.change('files', filteredFiles);
          dispatch(setFiles(filteredFiles));
        }
        dispatch(setUploading(true));
        setUploadError(null);
        const formData = new FormData();
        // Only submit files that are not succeeded
        filteredFiles.forEach((file: UploadFile) => {
          formData.append('files', file.file);
          formData.append('name', file.name);
          formData.append('fiscalYear', file.fiscalYear);
          formData.append('fiscalQuarter', file.fiscalQuarter);
          formData.append('publishedAt', file.publishedAt);
        });
        formData.append('ticker', company?.ticker || '');
        formData.append('category', category);
        formData.append('type', type);
        try {
          const res: UploadDocumentsResponse = await uploadDocuments(formData);
          if ('error' in res) {
            setUploadError(res.error);
          } else if (isResultsResponse(res)) {
            form.mutators.setUploadResults(res.results);
          }
        } catch (err: any) {
          // Try to get backend error message if available
          const backendError = err?.response?.data?.error || err.message;
          setUploadError(backendError);
        } finally {
          dispatch(setUploading(false));
        }
      }}
      mutators={{
        ...arrayMutators,
        setUploadResults: (
          results: { name: string; status: string; error?: string }[],
          state: any,
          utils: any,
        ) => {
          const flatResults = Array.isArray(results[0]) ? results.flat() : results;
          utils.changeValue(state, 'files', (files: UploadFile[]) =>
            files.map((f) => {
              const result = flatResults.find(
                (r: { name: string }) => normalizeName(r.name) === normalizeName(f.name),
              );
              if (!result) return { ...f, status: 'failed', error: 'Unknown error' };
              return {
                ...f,
                status: result.status,
                error: result.status === 'failed' ? result.error : undefined,
              };
            }),
          );
          dispatch(setFiles(state.formState.values.files));
        },
      }}
      initialValues={{
        files: reduxFiles,
        category,
        type,
        company,
      }}
      render={({ handleSubmit, form, values }) => {
        // Save values for useEffect
        lastValues.current = values;
        const typeOptions = values.category
          ? typeOptionsMap[values.category as DocumentCategory]
          : [];
        const fileNames = (values.files || []).map((f: UploadFile) => f.name.toLowerCase());
        const hasDuplicateNames = new Set(fileNames).size !== fileNames.length;
        const canSubmit =
          company &&
          values.category &&
          values.type &&
          values.files &&
          values.files.length > 0 &&
          values.files.every((f: UploadFile) => !validateFile(f)) &&
          !hasDuplicateNames &&
          !uploading &&
          (values.files || []).some((f: UploadFile) => f.status !== 'success');
        return (
          <form onSubmit={handleSubmit} className={styles.formContainer}>
            <Heading level={2}>1. Select Company</Heading>
            <TickerInput value={company} onChange={handleCompanyChange} />
            <Heading level={2}>2. Select Category</Heading>
            <Field name="category">
              {({ input }) => (
                <Select
                  options={categoryOptions}
                  value={input.value || categoryOptions[0].value}
                  onChange={(val) => {
                    input.onChange(val);
                    handleCategoryChange(val);
                  }}
                  placeholder="Select category"
                />
              )}
            </Field>
            {values.category && (
              <>
                <Heading level={2}>3. Select Type</Heading>
                <Field name="type">
                  {({ input }) => (
                    <Select
                      options={typeOptions}
                      value={input.value || (typeOptions[0] && typeOptions[0].value)}
                      onChange={(val) => {
                        input.onChange(val);
                        handleTypeChange(val);
                      }}
                      placeholder="Select type"
                    />
                  )}
                </Field>
              </>
            )}
            <Heading level={2}>4. Upload PDF Files</Heading>
            <DocumentDropzone onDrop={onDrop} reduxFiles={reduxFiles} uploading={uploading} />
            <FieldArray name="files">
              {(fieldArrayProps: FieldArrayRenderProps<UploadFile, HTMLElement>) => {
                dropzoneRef.current = fieldArrayProps;
                const { fields } = fieldArrayProps;
                return (
                  <div className={styles.fileList}>
                    {fields.map((name: string) => (
                      <Field name={name} key={name} validate={validateFile}>
                        {({ input }) => {
                          if (!input.value) return null;
                          const showNameError =
                            fileNames.filter(
                              (n: string) => n === (input.value!.name || '').toLowerCase(),
                            ).length > 1;
                          return (
                            <DocumentFileItem
                              file={input.value}
                              onChange={(updates) => input.onChange({ ...input.value, ...updates })}
                              showNameError={showNameError}
                              status={input.value.status || 'idle'}
                              error={input.value.error}
                            />
                          );
                        }}
                      </Field>
                    ))}
                    {hasDuplicateNames && (
                      <div className={styles.errorMsg}>Duplicate file names are not allowed.</div>
                    )}
                  </div>
                );
              }}
            </FieldArray>
            {uploadError && (
              <div className={styles.errorMsg} style={{ marginBottom: 12 }}>
                {uploadError}
              </div>
            )}
            <div className={styles.buttonContainer}>
              <Button
                className={styles.uploadBtn}
                type="submit"
                disabled={!canSubmit}
                loading={uploading}
              >
                Upload
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleClearForm(form)}
                disabled={uploading}
              >
                Clear Form
              </Button>
            </div>
          </form>
        );
      }}
    />
  );
};

export default DocumentUploadForm;
